import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import type { ClientNotification } from '../../types/clientNotification';
import { deleteClientNotification } from '../../services/clientNotificationService';
import { formatLinkedInTime } from './notificationFormatters';
import { NotificationAvatar } from './NotificationAvatar';
import { CWY_COLORS } from '../../theme/colors';

interface NotificationRowProps {
  notification: ClientNotification;
  onOpen: (notification: ClientNotification) => void;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
}

export const NotificationRow: React.FC<NotificationRowProps> = ({
  notification,
  onOpen,
  onMarkRead,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [hovered, setHovered] = useState(false);
  const isUnread = notification.status === 'unread';

  const handleDelete = async () => {
    setMenuAnchor(null);
    try {
      await deleteClientNotification(notification._id);
      onDelete(notification._id);
    } catch {
      // ignore
    }
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(notification)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: '100%',
        pl: isUnread ? 2.75 : 2.5,
        pr: 2,
        py: 1.75,
        border: 'none',
        borderBottom: `1px solid ${CWY_COLORS.border}`,
        bgcolor: isUnread ? 'var(--cwy-unreadBg, rgba(0, 113, 133, 0.08))' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        position: 'relative',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: isUnread ? 'var(--cwy-unreadHoverBg, rgba(0, 113, 133, 0.12))' : 'var(--cwy-hoverBg, rgba(15, 17, 17, 0.04))' },
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {isUnread && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 12,
            top: 28,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: CWY_COLORS.teal,
          }}
        />
      )}

      <Box sx={{ flexShrink: 0, pt: 0.25 }}>
        <NotificationAvatar notification={notification} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, pr: 0.5, pt: 0.25 }}>
        <Typography
          variant="body2"
          sx={{
            color: CWY_COLORS.textPrimary,
            fontSize: '0.875rem',
            lineHeight: 1.5,
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {notification.title}
          </Box>
          {notification.body ? (
            <>
              {' — '}
              <Box
                component="span"
                sx={{
                  fontWeight: isUnread ? 500 : 400,
                  color: isUnread ? CWY_COLORS.textPrimary : CWY_COLORS.textSecondary,
                }}
              >
                {notification.body}
              </Box>
            </>
          ) : null}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.25,
          flexShrink: 0,
          minWidth: 40,
          pt: 0.25,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: isUnread ? CWY_COLORS.teal : CWY_COLORS.textSecondary,
            fontSize: '0.75rem',
            fontWeight: isUnread ? 600 : 400,
            whiteSpace: 'nowrap',
          }}
        >
          {formatLinkedInTime(notification.createdAt)}
        </Typography>
        <IconButton
          size="small"
          aria-label="Notification options"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            color: CWY_COLORS.textSecondary,
            p: 0.25,
            opacity: hovered || Boolean(menuAnchor) ? 1 : 0,
            transition: 'opacity 0.15s ease',
          }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 160, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', bgcolor: CWY_COLORS.glassBgRaised },
          },
        }}
      >
        {isUnread && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onMarkRead(notification._id);
            }}
          >
            Mark as read
          </MenuItem>
        )}
        <MenuItem onClick={() => void handleDelete()} sx={{ color: CWY_COLORS.redCore }}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};
